import { useState, useCallback } from 'react'

import { useHistory, useLocation } from 'react-router-dom'
import qs from 'query-string'

import { loginWithGoogle, loginWithEmailAndPassword } from '../services/api'
import { useSetCredentials } from '../components/CredentialsProvider'
import { useSecurityConfig } from '../components/SecurityConfigProvider'
import { hasAccessToTheApplication } from '../services/validations'
import { isPartial } from '../services/token'

/* A React hook that provides helper functions to create a login page.
 */
export const useLogin = () => {
  const securityConfig = useSecurityConfig()
  const setSecurity = useSetCredentials()
  const location = useLocation()
  const history = useHistory()

  const { error: errorFromUrl } = qs.parse(location.search)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState(errorFromUrl || null)

  const [logingInWithGoogle, setLogingInWithGoogle] = useState(false)

  const mustCheckApplicationPermission = Boolean(securityConfig.application)

  const [
    logingInWithEmailAndPassword,
    setLogingInWithEmailAndPassword,
  ] = useState(false)

  const handleLoginWithGoogle = useCallback(() => {
    setLogingInWithGoogle(true)
    loginWithGoogle(securityConfig.redirectUri)
  }, [setLogingInWithGoogle])

  const handleLoginWithEmailAndPassword = useCallback(
    ({ email, password }: { email: string; password: string }) => {
      setLogingInWithEmailAndPassword(true)

      loginWithEmailAndPassword({
        username: email,
        password,
      })
        .then(({ user, token, refreshToken }) => {
          const notVerified = user.verificationStates.find(verification => !verification.value)
          if (notVerified) {
            return history.push(notVerified.status === "EMAIL_VERIFIED" ? securityConfig.emailVerificationPath : securityConfig.phoneVerificationPath)
          }
          if (
            !mustCheckApplicationPermission ||
            hasAccessToTheApplication(securityConfig.application, user)
          ) {
            setSecurity({ authentication: user, token, refreshToken }, 'local')

            if (isPartial(token)) {
              return history.push(securityConfig.twoFactorAuthPath)
            }

            history.push(securityConfig.successPath)
          } else {
            setLoginError('no_application_permission')
          }
        })
        .catch((error) => {
          setLoginError(error.response.data.message)
        })
        .finally(() => {
          setLogingInWithEmailAndPassword(false)
        })
    },
    [setLogingInWithEmailAndPassword, setSecurity, setLoginError]
  )

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value)
    },
    [setEmail]
  )

  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value)
    },
    [setPassword]
  )

  return {
    handleLoginWithGoogle,
    handleLoginWithEmailAndPassword,
    loginError,
    logingInWithGoogle,
    logingInWithEmailAndPassword,
    handlePasswordChange,
    handleEmailChange,
    email,
    password,
  }
}
